import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { formatLinksList } from "../../../../components/forms/common/subForms/LinksListField";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import useTextQuery from "../../../../hooks/query/useTextQuery";
import { Text } from "../../../../types/text";
import { Version } from "../../../../types/version";

interface VersionTextsViewSectionProps {
  version: Version;
}

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const getTextFields = (text: Text): ViewAccordionField[] => [
  {
    label: "Intellexus ID",
    value: <Typography variant="body1">{displayText(text?.published_id)}</Typography>,
  },
  {
    label: "Name",
    value: <Typography variant="body1">{displayText(text?.name)}</Typography>,
  },
  {
    label: "Digitization",
    value: <Typography variant="body1">{displayText(text?.digitization)}</Typography>,
  },
  {
    label: "Based On",
    value: <Typography variant="body1">{displayText(text?.based_on_type)}</Typography>,
  },
  {
    label: "Sources",
    value: <>{formatLinksList(text?.sources)}</>,
  },
  {
    label: "Text Names",
    value: (
      <Typography variant="body1">
        {Array.isArray(text?.text) && text.text.length > 0
          ? text.text.join(", ")
          : "N/A"}
      </Typography>
    ),
  },
];

const VersionTextsViewSection: React.FC<VersionTextsViewSectionProps> = ({
  version,
}) => {
  const { useFetchAll } = useTextQuery(version.system_language_type);
  const { data, isLoading, errorMessage } = useFetchAll({
    filters: {
      version_id: version._key,
      system_language_type: version.system_language_type,
    },
  });

  const items = useMemo<Text[]>(() => data?.data ?? [], [data]);

  return (
    <ConnectionAccordionSection<Text>
      title="Texts"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(text, index) => text?._key ?? `text-${index}`}
      getItemTitle={(text) => text?.published_id || text?.name || "Untitled Text"}
      getItemSubtitle={(text) => text?.name}
      getItemFields={(text) => getTextFields(text)}
      emptyText="No linked texts"
    />
  );
};

export default VersionTextsViewSection;
